import { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { SiteCodes } from 'shared-types'
import { Accordion } from '~/components/Accordion'
import { SectionContainer } from '~/components/SectionContainer'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { convertProductResourceUrl } from '~/helpers'
import { useBraze, usePage } from '~/hooks'
import { ProductReviews } from '~/sections/ProductDetails/ProductReviews'

import { ProductArticles } from './ProductArticles'
import styles from './ProductDetails.module.scss'
import {
  ProductDetailsProps,
  ProductDownloadsProps,
} from './ProductDetails.types'
import ProductDownloads from './ProductDownloads'
import ProductOverview from './ProductOverview'
import ProductSpecifications from './ProductSpecifications'
import { BrazeBanner } from '~/components'

const ProductDetails = ({
  articles,
  specification,
  overview,
  downloads,
  reviews,
  width = 'medium',
}: ProductDetailsProps) => {
  const { siteCode } = usePage()
  const [defaultValue, setDefaultValue] = useState(['Overview'])
  const { asPath } = useRouter()
  const { requestBannersRefresh } = useBraze()

  useEffect(() => {
    const hash = asPath.split('#')[1]
    if (hash === 'reviews') {
      setDefaultValue(['Overview', 'Reviews'])
    }
  }, [asPath])

  const formattedDownloads: ProductDownloadsProps = useMemo(() => {
    return {
      items: downloads?.items?.map((item) => {
        return {
          ...item,
          link: convertProductResourceUrl(item.link, siteCode),
        }
      }),
    }
  }, [downloads, siteCode])

  const panels = [
    {
      name: 'Overview',
      header: (
        <Typography variant={TypographyVariant.Heading4} tag={TypographyTag.h2}>
          Overview
        </Typography>
      ),
      content: <ProductOverview {...overview} />,
    },
  ]

  if (specification?.items?.length > 0) {
    panels.push({
      name: 'Specifications',
      header: (
        <Typography variant={TypographyVariant.Heading4} tag={TypographyTag.h2}>
          Specifications
        </Typography>
      ),
      content: <ProductSpecifications {...specification} />,
    })
  }

  if (formattedDownloads?.items?.length > 0) {
    panels.push({
      name: 'Downloads',
      header: (
        <Typography variant={TypographyVariant.Heading4} tag={TypographyTag.h2}>
          Downloads
        </Typography>
      ),
      content: <ProductDownloads {...formattedDownloads} />,
    })
  }

  if (articles?.items?.length > 0) {
    panels.push({
      name: 'Articles',
      header: (
        <Typography variant={TypographyVariant.Heading4} tag={TypographyTag.h2}>
          Articles
        </Typography>
      ),
      content: <ProductArticles {...articles} />,
    })
  }

  if (reviews?.bazaarVoice?.productIdentifier && siteCode !== SiteCodes.RTM) {
    panels.push({
      name: 'Reviews',
      header: (
        <Typography variant={TypographyVariant.Heading4} tag={TypographyTag.h2}>
          <div id='reviews'>Reviews</div>
        </Typography>
      ),
      content: <ProductReviews bazaarVoice={reviews.bazaarVoice} />,
    })
  }

  return (
    <SectionContainer width={width}>
      <BrazeBanner placementId='pdp-slot-1'/>
      <div className={classNames(styles.root, 'border-t mb-14 md:mb-20')}>
        <Accordion
          theme={{
            title: styles.title,
            content_inner: styles.content_inner,
            control: 'px-0 py-10 !bg-white',
            icon: styles.icon,
          }}
          variant='default'
          panels={panels}
          multiple
          defaultValue={defaultValue}
        />
      </div>
      <BrazeBanner placementId='pdp-slot-2' />
      <BrazeBanner placementId='pdp-slot-3' />
    </SectionContainer>
  )
}

export { ProductDetails }
